/**
 * QueueService - Business logic layer for queue management
 * Handles validation and orchestration for queue operations
 *
 * Part of the 3-layer architecture refactor
 * Repository → Service → React Query Hooks
 */

import { BaseService, ServiceError } from './BaseService';
import { repositories } from '../repositories';
import type {
  CallQueue,
  AgentQueueAssignment,
  PhoneQueueAssignment,
  CallCenterAgent,
  QueueVoicemailSettings,
  UpsertQueueVoicemailSettingsInput
} from '../repositories/QueueRepository';

interface QueueCreateInput {
  company_name: string;
  name?: string;
  priority?: number;
  max_wait_time?: number;
  is_active?: boolean;
}

interface QueueUpdateInput {
  company_name?: string;
  name?: string;
  priority?: number;
  max_wait_time?: number;
  is_active?: boolean;
}

interface QueueStats {
  total_calls: number;
  answered: number;
  abandoned: number;
  avg_wait_time: number; // historical average wait (completed calls today)
  avg_handle_time: number;
  service_level: number;
  calls_in_queue: number; // active calls right now
  longest_wait_seconds: number; // live max wait among active callers
  average_wait_seconds: number; // live average wait among active callers
  max_wait_seconds_today: number; // historical max wait among completed calls today
}

/**
 * Service for managing call queues
 */
export class QueueService extends BaseService {
  /**
   * Get all queues
   */
  async getAllQueues(): Promise<CallQueue[]> {
    return await repositories.queues.findQueues();
  }

  /**
   * Get active queues only
   */
  async getActiveQueues(): Promise<CallQueue[]> {
    return await repositories.queues.findQueues({ is_active: true });
  }

  /**
   * Get all phone number assignments across all queues
   */
  async getAllPhones(): Promise<PhoneQueueAssignment[]> {
    return await repositories.queues.getAllPhoneAssignments();
  }

  /**
   * Get queue by ID
   */
  async getQueue(queueId: string): Promise<CallQueue | null> {
    try {
      return await repositories.queues.findById(queueId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get queue with detailed stats
   */
  async getQueueWithDetails(queueId: string) {
    try {
      return await repositories.queues.findQueueWithDetails(queueId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create a new queue
   */
  async createQueue(input: QueueCreateInput): Promise<CallQueue> {
    try {
      const companyName = input.company_name?.trim();
      if (!companyName) {
        throw ServiceError.validation([
          {
            field: 'company_name',
            message: 'Company name is required'
          }
        ]);
      }

      // Generate system name from company name if not provided
      const systemName = (input.name?.trim() ||
        companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      ).slice(0, 64);

      const queueData: Partial<CallQueue> = {
        name: systemName,
        company_name: companyName,
        company_id: null,
        max_wait_time: input.max_wait_time || 300,
        priority: input.priority || 50,
        is_active: input.is_active !== undefined ? input.is_active : true
      };

      const queue = await repositories.queues.createQueue(queueData);
      await this.emit('queue:created', queue);
      return queue;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update queue configuration
   */
  async updateQueue(queueId: string, updates: QueueUpdateInput): Promise<CallQueue> {
    try {
      // Validate priority range
      if (updates.priority !== undefined) {
        if (updates.priority < 0 || updates.priority > 100) {
          throw ServiceError.validation([
            {
              field: 'priority',
              message: 'Priority must be between 0 and 100'
            }
          ]);
        }
      }

      // Validate max wait time
      if (updates.max_wait_time !== undefined && updates.max_wait_time < 1) {
        throw ServiceError.validation([
          {
            field: 'max_wait_time',
            message: 'Max wait time must be at least 1 second'
          }
        ]);
      }

      const queue = await repositories.queues.updateQueue(queueId, updates);
      await this.emit('queue:updated', queue);
      return queue;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Toggle queue active status
   */
  async toggleQueueStatus(queueId: string): Promise<CallQueue> {
    try {
      const queue = await repositories.queues.toggleQueueStatus(queueId);
      await this.emit('queue:status_changed', queue);
      return queue;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Delete a queue
   */
  async deleteQueue(queueId: string): Promise<void> {
    try {
      // Check if queue has active agents or phones
      const agents = await repositories.queues.getQueueAgents(queueId);
      if (agents.length > 0) {
        throw ServiceError.conflict('Cannot delete queue with assigned agents');
      }

      const phones = await repositories.queues.getQueuePhones(queueId);
      if (phones.length > 0) {
        throw ServiceError.conflict('Cannot delete queue with assigned phone numbers');
      }

      await repositories.queues.deleteQueue(queueId);
      await this.emit('queue:deleted', { queueId });
    } catch (error) {
      this.handleError(error);
    }
  }

  // ==================== AGENT MANAGEMENT ====================

  /**
   * Get all agents
   * Source of truth: agents table (not app_users)
   */
  async getAllAgents(): Promise<CallCenterAgent[]> {
    // Mirror /users: source from app_users via QueueRepository
    // This ensures the "Available Agents" list matches the Users page
    const agents = await repositories.queues.getAllAgents();
    return (agents || []) as CallCenterAgent[];
  }

  /**
   * Get agents assigned to a queue
   */
  async getQueueAgents(queueId: string) {
    try {
      return await repositories.queues.getQueueAgents(queueId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Assign agent to queue
   */
  async assignAgentToQueue(
    agentOrUserId: string,
    queueId: string,
    priority: number = 50
  ): Promise<AgentQueueAssignment> {
    try {
      // Determine the proper agents.id to store in agent_queue_assignments
      let agentIdToAssign = agentOrUserId;

      // 1) Check if id is already an agents.id
      const existingAgentById = await repositories.agents.findById(agentOrUserId).catch(() => null);

      if (!existingAgentById) {
        // 2) Treat as user_id -> find agent by user_id
        const existingByUser = await repositories.agents.getByUserId(agentOrUserId).catch(() => null);
        if (existingByUser) {
          agentIdToAssign = existingByUser.id;
        } else {
          // 3) Create agent row for this user, sourcing display info from app_users
          const user = await repositories.users.findById(agentOrUserId);
          const displayName =
            (user as any)?.name ||
            (user as any)?.full_name ||
            (user as any)?.email?.split?.('@')?.[0] ||
            'Agent';

          const newAgent = await repositories.agents.create({
            user_id: agentOrUserId,
            name: displayName,
            email: (user as any)?.email || null,
            phone_number: null,
            current_status: 'available',
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          } as any);

          agentIdToAssign = (newAgent as any).id;
        }
      }

      // Check if already assigned (now using agents.id)
      const isAssigned = await repositories.queues.isAgentInQueue(agentIdToAssign, queueId);
      if (isAssigned) {
        throw ServiceError.conflict('Agent is already assigned to this queue');
      }

      const assignment = await repositories.queues.assignAgentToQueue(
        agentIdToAssign,
        queueId,
        priority
      );
      await this.emit('agent:assigned', { agentId: agentIdToAssign, queueId });
      return assignment;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Remove agent from queue
   */
  async removeAgentFromQueue(assignmentId: string): Promise<void> {
    try {
      await repositories.queues.removeAgentFromQueue(assignmentId);
      await this.emit('agent:removed', { assignmentId });
    } catch (error) {
      this.handleError(error);
    }
  }

  // ==================== PHONE MANAGEMENT ====================

  /**
   * Get phone assignments for a queue
   */
  async getQueuePhones(queueId: string): Promise<PhoneQueueAssignment[]> {
    try {
      return await repositories.queues.getQueuePhones(queueId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Assign phone number to queue
   */
  async assignPhoneToQueue(
    phoneNumber: string,
    queueId: string,
    isPrimary: boolean = true
  ): Promise<PhoneQueueAssignment> {
    try {
      // Validate phone number format (basic E.164 check)
      if (!phoneNumber.startsWith('+')) {
        throw ServiceError.validation([
          {
            field: 'phoneNumber',
            message: 'Phone number must be in E.164 format (start with +)'
          }
        ]);
      }

      const assignment = await repositories.queues.assignPhoneToQueue(
        phoneNumber,
        queueId,
        isPrimary
      );
      await this.emit('phone:assigned', { phoneNumber, queueId });
      return assignment;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Remove phone assignment
   */
  async removePhoneFromQueue(assignmentId: string): Promise<void> {
    try {
      await repositories.queues.removePhoneFromQueue(assignmentId);
      await this.emit('phone:removed', { assignmentId });
    } catch (error) {
      this.handleError(error);
    }
  }

  // ==================== ROUTING POLICY + VOICEMAIL ====================

  /**
   * Update routing policy fields for a queue
   */
  async updateQueueRoutingPolicy(
    queueId: string,
    policy: {
      routing_default_mode?: 'human_first' | 'human_only' | 'ai_only' | 'direct_agent' | null;
      default_fallback_ai_agent_id?: string | null;
      default_fallback_timeout_seconds?: number | null;
      direct_agent_id?: string | null;
      ring_strategy?: 'simultaneous' | 'round_robin' | null;
    }
  ): Promise<CallQueue> {
    try {
      // Basic validation of combinations
      if (policy.routing_default_mode === 'direct_agent' && !policy.direct_agent_id) {
        throw ServiceError.validation([
          { field: 'direct_agent_id', message: 'Direct agent is required for direct_agent mode' }
        ]);
      }
      if (policy.routing_default_mode === 'human_first') {
        if (
          policy.default_fallback_timeout_seconds !== null &&
          policy.default_fallback_timeout_seconds !== undefined
        ) {
          const secs = policy.default_fallback_timeout_seconds;
          if (secs < 0) {
            throw ServiceError.validation([
              { field: 'default_fallback_timeout_seconds', message: 'Fallback timeout must be >= 0 seconds' }
            ]);
          }
        }
      }
      // Validate ring strategy
      if (policy.ring_strategy && !['simultaneous', 'round_robin'].includes(policy.ring_strategy)) {
        throw ServiceError.validation([
          { field: 'ring_strategy', message: 'Invalid ring strategy' }
        ]);
      }

      const updated = await repositories.queues.updateQueueRoutingPolicy(queueId, policy);
      await this.emit('queue:routing_updated', { queueId, policy });
      return updated;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get queue voicemail settings
   */
  async getQueueVoicemailSettings(queueId: string): Promise<QueueVoicemailSettings | null> {
    try {
      return await repositories.queues.getQueueVoicemailSettings(queueId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Upsert queue voicemail settings
   */
  async upsertQueueVoicemailSettings(
    input: UpsertQueueVoicemailSettingsInput
  ): Promise<QueueVoicemailSettings> {
    try {
      const settings = await repositories.queues.upsertQueueVoicemailSettings(input);
      await this.emit('queue:voicemail_updated', { queueId: input.queue_id });
      return settings;
    } catch (error) {
      this.handleError(error);
    }
  }

  // ==================== STATISTICS ====================

  /**
   * Get queue statistics for today
   */
  async getQueueStats(queueName: string): Promise<QueueStats> {
    try {
      return await repositories.queues.getQueueStats(queueName);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get stats for all queues
   */
  async getAllQueueStats(): Promise<Map<string, QueueStats>> {
    const queues = await repositories.queues.findQueues();
    const statsMap = new Map<string, QueueStats>();

    for (const queue of queues) {
      const stats = await repositories.queues.getQueueStats(queue.name);
      statsMap.set(queue.name, stats);
    }

    return statsMap;
  }
}

// Export singleton instance
export const queueService = new QueueService();
